import { useCallback, useEffect, useState } from "react";
import { isAxiosError } from "axios";
import { useNavigate } from "@tanstack/react-router";

import { useAppDispatch } from "@/store/hooks";
import { initializeRequests } from "@/store/slices/requestsSlice";
import { initializeTables } from "@/store/slices/advancedTableSlice";
import { initializeWallpaperStore } from "@/store/slices/wallpaperSlice";
import { userQueryOptions } from "@/hooks/queries/userQuery";
import { UserContext } from "@/lib/context";
import { emptyStorageUser } from "@/lib/storage";
import { Authentication } from "../Authentication";
import { UserModal } from "../User/Modal";
import { LOCALSTORAGE_KEY } from "@/lib/constants";
import { useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/query";
import { toast } from "sonner";
import { ScreenSpinner } from "../ui/spinner";
import { identifyUser, resetUser } from "@/lib/analytics";
import { maybeTrackDay2Return } from "@/lib/onboarding";
import { SessionPayload, tokenManager } from "@/lib/auth/token-manager";
import { finishPendingTrelloConnect } from "@/lib/auth/connect";

export const UserProvider = ({
  resetPasswordToken,
  changePasswordToken,
  children,
}: {
  resetPasswordToken?: string;
  changePasswordToken?: string;
  children: React.ReactNode;
}) => {
  const [loading, setLoading] = useState(true);
  const [accessToken, setSessionKey] = useState<string | null>(
    tokenManager.getState().sessionKey,
  );
  const [isAuthenticationModalOpen, setAuthenticationModalOpen] =
    useState<boolean>(!!resetPasswordToken);
  const [isUserModalOpen, setUserModalOpen] = useState<boolean>(false);
  const [isCreateProfileModalOpen, setCreateProfileModalOpen] = useState(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const resetLocalState = useCallback(async () => {
    await emptyStorageUser();
    setUserModalOpen(false);
    resetUser();
    queryClient.clear();
  }, []);

  const logout = useCallback(async () => {
    await tokenManager.logout();
    await resetLocalState();
    setAuthenticationModalOpen(true);
  }, [resetLocalState]);

  const {
    data: user,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    ...userQueryOptions(accessToken),
    enabled: !!accessToken,
    retry(failureCount, error) {
      // A dead session is already ended by the request interceptor; a retry
      // must never sign the session out on the server.
      if (
        isAxiosError(error) &&
        (error.response?.status === 401 || error.response?.status === 403)
      ) {
        return false;
      }

      if (failureCount >= 2) {
        toast.error(
          "Failed to load user profile after multiple attempts, please check your internet connection and try again.",
          {
            action: {
              label: "Try again",
              onClick: () => {
                refetch();
              },
            },
          },
        );
        return false;
      }

      return true;
    },
  });

  const setSession = useCallback(
    async (session: SessionPayload) => {
      await tokenManager.applySession(session);

      setTimeout(() => {
        const key = `${LOCALSTORAGE_KEY}-join-team-token`;
        const joinTeamToken = localStorage.getItem(key);
        if (joinTeamToken) {
          localStorage.removeItem(key);
          navigate({
            to: "/team/join",
            search: { token: joinTeamToken },
          });
        }
      }, 1000);
    },
    [navigate],
  );

  useEffect(() => {
    let previous = tokenManager.getState().sessionKey;
    const unsubscribe = tokenManager.subscribe(state => {
      setSessionKey(state.sessionKey);
      // Signed out by another tab, another device or an expired session.
      if (previous && !state.sessionKey) {
        void resetLocalState();
        setAuthenticationModalOpen(true);
      }
      previous = state.sessionKey;
    });

    tokenManager.ready.then(() => {
      setSessionKey(tokenManager.getState().sessionKey);
      if (tokenManager.getState().sessionKey && changePasswordToken) {
        setUserModalOpen(true);
      }
      const notice = tokenManager.consumeNotice();
      if (notice) {
        toast.info(notice);
        setAuthenticationModalOpen(true);
      }
      setLoading(false);
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user?.new_user) {
      setCreateProfileModalOpen(true);
    }
  }, [user]);

  useEffect(() => {
    if (user && accessToken) {
      maybeTrackDay2Return();
      void finishPendingTrelloConnect().then(connected => {
        if (connected) {
          toast.success("Trello connected");
          refetch();
        }
      });
    }
  }, [user, accessToken, refetch]);

  useEffect(() => {
    if (!user || !accessToken) {
      return;
    }

    identifyUser({
      distinctId: user.email,
      properties: {
        email: user.email,
        displayName: user.displayName,
        ownedTeamsCount: user.ownedTeams.length,
        joinedTeamsCount: user.joinedTeams.length,
        pendingTeamsCount: user.pendingTeams.length,
      },
    });
  }, [accessToken, user]);

  useEffect(() => {
    dispatch(initializeWallpaperStore());
    dispatch(initializeRequests());
    dispatch(initializeTables());
  }, [accessToken, dispatch]);

  return (
    <UserContext.Provider
      value={{
        user: user || null,
        syncUser: refetch,
        accessToken,
        setSession,
        isAuthenticationModalOpen,
        setAuthenticationModalOpen,
        isUserModalOpen,
        setUserModalOpen,
        isCreateProfileModalOpen,
        setCreateProfileModalOpen,
        isLoading,
        isError,
        error,
        logout,
      }}
    >
      {loading ? (
        <div className="bg-background flex min-h-screen w-screen items-center justify-center">
          <div className="flex flex-col gap-4">
            <ScreenSpinner />
            <span className="text-muted-foreground animate-pulse">
              Fetching account information
            </span>
          </div>
        </div>
      ) : (
        children
      )}
      <Authentication resetPasswordToken={resetPasswordToken} />
      <UserModal
        open={isUserModalOpen}
        onOpenChange={setUserModalOpen}
        changePasswordToken={changePasswordToken}
      />
    </UserContext.Provider>
  );
};
